import {
    IRequirement,
    IRobotOption,
    IRobotSelectorRecommendation,
    IRobotSelectorTaxonomyLabels,
} from '../models/robot-selector.model';

export interface ITaxonomyOption {
    token: string;
    label: string;
}

export interface ITaxonomyOptions {
    industries: ITaxonomyOption[];
    applications: ITaxonomyOption[];
}

export type ITaxonomySelection = Pick<IRequirement, 'industry' | 'application'>;

export function findRecommendation(
    recommendations: readonly IRobotSelectorRecommendation[],
    selection: ITaxonomySelection,
): IRobotSelectorRecommendation | undefined {
    return recommendations.find(
        (recommendation) =>
            (recommendation.industry ?? null) === selection.industry &&
            (recommendation.application ?? null) === selection.application,
    );
}

/** The robots picked for exactly this selection, in the order the editors authored. */
export function recommend(
    robots: readonly IRobotOption[],
    recommendations: readonly IRobotSelectorRecommendation[],
    selection: ITaxonomySelection,
): IRobotOption[] {
    const recommendation = findRecommendation(recommendations, selection);
    if (!recommendation) {
        return [];
    }

    const robotsById = new Map(robots.map((robot) => [robot.id, robot]));

    return recommendation.robotIds
        .map((id) => robotsById.get(id))
        .filter((robot): robot is IRobotOption => robot !== undefined);
}

/**
 * The options each dropdown can move to without leaving the pick lists: an industry is offered when there
 * is an entry for it together with the currently selected application (or on its own when none is), and
 * the same for applications. Every reachable selection therefore has picks.
 */
export function recommendationOptions(
    recommendations: readonly IRobotSelectorRecommendation[],
    labels: IRobotSelectorTaxonomyLabels | null,
    selection: ITaxonomySelection,
): ITaxonomyOptions {
    const industries = recommendations
        .filter((recommendation) => (recommendation.application ?? null) === selection.application)
        .map((recommendation) => recommendation.industry);
    const applications = recommendations
        .filter((recommendation) => (recommendation.industry ?? null) === selection.industry)
        .map((recommendation) => recommendation.application);

    return {
        industries: collect(industries, labels?.industries),
        applications: collect(applications, labels?.applications),
    };
}

function collect(
    tokens: readonly (string | null | undefined)[],
    labels: Record<string, string> | undefined,
): ITaxonomyOption[] {
    const options = new Map<string, ITaxonomyOption>();

    for (const token of tokens) {
        const label = token ? labels?.[token] : undefined;

        if (token && label && !options.has(token)) {
            options.set(token, { token, label });
        }
    }

    return [...options.values()].sort((left, right) => left.label.localeCompare(right.label));
}
